import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  AdminOrganization,
  CreatedRegistrationCode,
} from "src/Types/auth.types";
import { CommunityBoundary } from "src/Types/monitoring.types";
import { createAdminRegistrationCode } from "src/utils/apicalls";
import { useTranslation } from "react-i18next";
import LocationSearch from "src/Pages/Dashboard/LocationSearch";
import { getApiErrorMessage } from "src/utils/apiError";

type CitySource = "new" | "existing";

const EXPIRY_OPTIONS = [
  { value: "", labelKey: "admin.keyDialog.expiryNever" },
  { value: "7", labelKey: "admin.keyDialog.expiryDays", count: 7 },
  { value: "30", labelKey: "admin.keyDialog.expiryDays", count: 30 },
  { value: "90", labelKey: "admin.keyDialog.expiryDays", count: 90 },
  { value: "365", labelKey: "admin.keyDialog.expiryYear" },
];

interface CreateKeyDialogProps {
  open: boolean;
  organizations: AdminOrganization[];
  onClose: () => void;
  onCreated: () => void;
}

type CreateKeyFormProps = Omit<CreateKeyDialogProps, "open"> & {
  onSubmittingChange: (submitting: boolean) => void;
};

/** Mounted fresh each time the dialog opens, so every open starts from a clean form. */
const CreateKeyForm: React.FC<CreateKeyFormProps> = ({
  organizations,
  onClose,
  onCreated,
  onSubmittingChange,
}) => {
  const { t } = useTranslation();
  const [source, setSource] = useState<CitySource>(
    organizations.length > 0 ? "existing" : "new"
  );
  const [community, setCommunity] = useState<CommunityBoundary | null>(null);
  const [existing, setExisting] = useState<AdminOrganization | null>(null);
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedRegistrationCode | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    onSubmittingChange(submitting);
  }, [onSubmittingChange, submitting]);

  const canSubmit =
    !submitting && (source === "new" ? Boolean(community) : Boolean(existing));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    const common = {
      label: label.trim() || undefined,
      expiresInDays: expiry ? Number(expiry) : undefined,
    };
    try {
      const result =
        source === "new" && community
          ? await createAdminRegistrationCode({ ...common, community })
          : await createAdminRegistrationCode({
              ...common,
              organizationId: existing!.id,
            });
      setCreated(result);
      onCreated();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, "admin.keyDialog.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <DialogTitle id="create-key-title">
        {created ? t("admin.keyDialog.createdTitle") : t("admin.keyDialog.title")}
      </DialogTitle>

      {created ? (
        <>
          <DialogContent>
            <Stack spacing={2}>
              <Alert severity="warning">
                {t("admin.keyDialog.copyWarning")}
              </Alert>
              <Typography color="text.secondary">
                {t(
                  created.registrationCode.expiresAt
                    ? "admin.keyDialog.createdBodyExpiring"
                    : "admin.keyDialog.createdBody",
                  { name: created.organization.name }
                )}
              </Typography>
              <TextField
                label={t("admin.keyDialog.keyLabel")}
                value={created.code}
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                    sx: {
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      letterSpacing: "0.08em",
                      fontSize: "1.1rem",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? t("common.copied") : t("admin.keyDialog.copy")}>
                          <IconButton
                            onClick={handleCopy}
                            aria-label={copied ? t("admin.keyDialog.copiedLabel") : t("admin.keyDialog.copy")}
                          >
                            {copied ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <ContentCopyIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button variant="contained" onClick={onClose}>
              {t("admin.keyDialog.done")}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              {error && (
                <Alert severity="error" role="alert">
                  {error}
                </Alert>
              )}
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={source}
                onChange={(_event, value: CitySource | null) => {
                  if (value) setSource(value);
                }}
                aria-label={t("admin.keyDialog.cityToggle")}
              >
                <ToggleButton value="existing" disabled={organizations.length === 0}>
                  {t("admin.keyDialog.existingCity")}
                </ToggleButton>
                <ToggleButton value="new">{t("admin.keyDialog.newCity")}</ToggleButton>
              </ToggleButtonGroup>

              {source === "existing" ? (
                <Autocomplete
                  options={organizations}
                  value={existing}
                  onChange={(_event, value) => setExisting(value)}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Stack>
                        <Typography>{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.boundaryName || t("admin.keyDialog.noBoundary")}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label={t("admin.accounts.city")} required />
                  )}
                />
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    {t("admin.keyDialog.newCityHelp")}
                  </Typography>
                  <LocationSearch onCommunitySelect={setCommunity} />
                  {community && (
                    <Alert
                      icon={<CheckCircleIcon />}
                      severity="success"
                      sx={{ mt: 1.5 }}
                    >
                      {community.name}
                    </Alert>
                  )}
                </Box>
              )}

              <TextField
                label={t("admin.keyDialog.label")}
                placeholder={t("admin.keyDialog.labelPlaceholder")}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                helperText={t("admin.keyDialog.labelHelp")}
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
              <TextField
                select
                label={t("admin.keyDialog.expiry")}
                value={expiry}
                onChange={(event) => setExpiry(event.target.value)}
              >
                {EXPIRY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey, { count: option.count })}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={onClose} disabled={submitting}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              startIcon={
                submitting ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {submitting ? t("admin.keyDialog.creating") : t("admin.keyDialog.submit")}
            </Button>
          </DialogActions>
        </>
      )}
    </>
  );
};

const CreateKeyDialog: React.FC<CreateKeyDialogProps> = ({
  open,
  organizations,
  onClose,
  onCreated,
}) => {
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="create-key-title"
    >
      <CreateKeyForm
        organizations={organizations}
        onClose={onClose}
        onCreated={onCreated}
        onSubmittingChange={setSubmitting}
      />
    </Dialog>
  );
};

export default CreateKeyDialog;
